import { useRef, useLayoutEffect, useEffect, useState, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { QUERY_FOR_PHONE_WIDTH } from 'web-ui-kit/media';

function useValueHasChanged(value) {
  var hasChangedState = useRef({
    lastState: value,
    hasChanged: false
  });

  if (value !== hasChangedState.current.lastState) {
    hasChangedState.current.hasChanged = true;
  }

  return !hasChangedState.current.hasChanged;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
/**
 * Isomorphic useLayoutEffect component that will work as usual for CSR
 * and will suppress the bogus react warning for SSR.
 *
 * Usage:
 * import useLayoutEffect from 'useIsomorphicLayoutEffect'
 *
 * Note: make sure to call the import 'useLayoutEffect' as shown in the Usage
 * example above so that eslint will recognize it and run the normal react
 * hooks linting rules on the effect.
 *
 * See: https://medium.com/@alexandereardon/uselayouteffect-and-ssr-192986cdcf7a
 */
// in SSR environments. Neither useEffect nor useLayoutEffect are run on the
// server and they have the same type signature so its a good workaround.


var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
/**
 * Performance safe window resize event listener hook.
 * Resize callbacks are throttled using lodash throttle and the
 * event listeners are removed when the component unmounts.
 */

function useSafeResizeEventListener(_ref) {
  var _ref$listen = _ref.listen,
      listen = _ref$listen === void 0 ? true : _ref$listen,
      _ref$onResize = _ref.onResize,
      onResize = _ref$onResize === void 0 ? function () {} : _ref$onResize;
  useEffect(function () {
    if (listen) {
      var handleResize = throttle(function () {
        onResize();
      });
      window.addEventListener('resize', handleResize);
      return function () {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [listen, onResize]);
}

var STATE_NEITHER = ''; // default empty state

/**
 * SSR/Hydrate friendly hook for deciding which of two components
 * to render based on the result of evaluating a CSS media query string.
 *
 * This hook is useful for the situation where we want to render
 * completely different components depending on viewport dimensions
 * that can be targeted with a CSS media query AND it would not be
 * performant to just render both out and apply display:none media
 * queries. For example, if one of the component has expensive side
 * effects and we only want to render if its actually going to be visible.
 *
 * This hook is likely not the best choice when the component displayed
 * is important for SEO purposes. In that scenario its best to render out
 * both components and add display:none media queries to both to achieve
 * the desired user visible effect.
 *
 * For SSR, we must choose an initial state that makes sense in the
 * context of the component where this hook is being used. Prefer the
 * default neither state if possible which should result in neither
 * component rendering for SSR/Hydrate renders (once app finishes hydrating the
 * regular CSR will occur and desired component will render). If the matchesState
 * or notMatchesState is chosen, then you will likely need to add additional
 * display:none media queries to the actual components to prevent flicker when the
 * initialState guess was wrong. For a regular CSR, the desired component should
 * render as expected with no flicker.
 *
 * This hook returns the current state to render, it is up to the hook
 * user to then render the desired component based on the current state.
 * The hook will always return one of 3 states:
 *  (1) neither state (i.e. empty string)
 *  (2) matchesState (string provided by hook user)
 *  (3) notMatchesState (string provided by hook user)
 *
 * EXAMPLE USAGE:
 * export default function Carousel ({ images = []}) {
 *   const queryPhoneDown = '(max-width: 599px)';
 *   const { componentState: carouselState } = useComponentStateToggle({
 *     matchesState: 'carousel-swipeable',
 *     notMatchesState: 'carousel-thumbnails',
 *     query: queryPhoneDown
 *   });
 *
 *   return (
 *      <Container>
 *        ... (other components) ...
 *        {carouselState === 'carousel-swipeable' && <SwipeableCarousel images={images} />}
 *        {carouselState === 'carousel-thumbnails' && <ThumbnailCarousel images={images} />}
 *        ... (other components) ...
 *      </Container>
 *   ) ;
 * }
 */

function useComponentStateToggle() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$initialState = _ref.initialState,
      initialState = _ref$initialState === void 0 ? STATE_NEITHER : _ref$initialState,
      _ref$matchesState = _ref.matchesState,
      matchesState = _ref$matchesState === void 0 ? 'matches' : _ref$matchesState,
      _ref$notMatchesState = _ref.notMatchesState,
      notMatchesState = _ref$notMatchesState === void 0 ? 'not-matches' : _ref$notMatchesState,
      _ref$query = _ref.query,
      query = _ref$query === void 0 ? 'all' : _ref$query,
      _ref$_useLayoutEffect = _ref._useLayoutEffect,
      _useLayoutEffect = _ref$_useLayoutEffect === void 0 ? useIsomorphicLayoutEffect : _ref$_useLayoutEffect; // Validate initial render state.


  var firstRenderState = [STATE_NEITHER, matchesState, notMatchesState].includes(initialState) ? initialState : STATE_NEITHER; // The current state.

  var _useState = useState(firstRenderState),
      _useState2 = _slicedToArray(_useState, 2),
      componentState = _useState2[0],
      setComponentState = _useState2[1]; // This function computes and then updates the current component state.


  var updateComponentState = useCallback(function () {
    // Run media query and update component state.
    if (window.matchMedia(query).matches) {
      setComponentState(matchesState);
    } else {
      setComponentState(notMatchesState);
    }
  }, [matchesState, notMatchesState, setComponentState, query]); // Set our component state on initial client side render.

  _useLayoutEffect(function () {
    updateComponentState();
  }, []); // Update our component state on resize events.


  useSafeResizeEventListener({
    onResize: updateComponentState
  }); // Return the current component state.
  // Hook users should switch on this value to decide which component variant to render.

  return {
    componentState: componentState
  };
}
/**
 * Keys for window states
 * with the allowed time in seconds between activity before switching to each state
 * e.g.
 * Between 0 and 5 minutes activity is considered active
 * Between 5 and 15 minutes activity is considered low
 * After 15 minutes the page is considered Inactive
 */


var WINDOW_ACTIVITY_STATE = {
  Active: 300,
  // 5 minutes
  Low: 600,
  // 10 minutes
  Inactive: 900 // 15 minutes

};
var DEFAULT_LOOP_TIME = 60000; // one minute

var isClientSide = function isClientSide() {
  return typeof window !== 'undefined';
};
/**
 * This is a hook used to track whether this page is active
 * @param activityLoopTime How often you want the loop to check the activity state
 * @param windowActivityStates Possible states for the window activity in the form
 * of a key/value dictionary there are two states that are required
 *  1) Active
 *  2) Inactive
 *
 * The value is the number of seconds between the last activity update that after
 * passed will return the next state
 * @returns Key from windowActivityStates such as 'Active', 'Inactive'
 */


function useDocumentActive() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$activityLoopTime = _ref.activityLoopTime,
      activityLoopTime = _ref$activityLoopTime === void 0 ? DEFAULT_LOOP_TIME : _ref$activityLoopTime,
      _ref$windowActivitySt = _ref.windowActivityStates,
      windowActivityStates = _ref$windowActivitySt === void 0 ? WINDOW_ACTIVITY_STATE : _ref$windowActivitySt;

  var activityLoopInterval = useRef(null);
  var lastActiveTime = useRef(new Date());

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      visible = _useState2[0],
      setVisible = _useState2[1];

  var _useState3 = useState('Active'),
      _useState4 = _slicedToArray(_useState3, 2),
      activityState = _useState4[0],
      setActivityState = _useState4[1];

  var runActivityLoop = function runActivityLoop() {
    if (!isClientSide()) {
      return;
    } // Calculate the number of seconds since the last time there was activity on this page


    var secondsSinceActivity = Math.floor(Math.abs(new Date() - lastActiveTime.current) / 1000); // Loop through the keys and determine the new state of activity

    var newState = 'Inactive';

    for (var _i = 0, _Object$keys = Object.keys(windowActivityStates); _i < _Object$keys.length; _i++) {
      var key = _Object$keys[_i];

      if (secondsSinceActivity <= windowActivityStates[key]) {
        newState = key;
        break;
      }
    } // Only considered active if the page is not hidden


    newState = visible ? newState : 'Inactive';

    if (newState !== activityState) {
      setActivityState(newState);
    }
  }; // We do not need thesee updates to be very frequent
  // so we just log an activity callback every 5 seconds


  var activityCallback = useCallback(throttle(function () {
    if (!isClientSide()) {
      return;
    }

    lastActiveTime.current = new Date(); // If our previous state was inactive and the activityLoop was stopped
    // restart the loop

    if (!activityLoopInterval.current) {
      activityLoopInterval.current = setInterval(runActivityLoop, activityLoopTime);
    }
  }, 5000), [lastActiveTime]); // Handle visibility changes on the window

  var visibilityCallback = useCallback(function () {
    if (!isClientSide()) {
      return;
    }

    setVisible(document.visibilityState === 'visible');
  }, [setVisible]); // Update and remove document listeners

  useEffect(function () {
    if (!isClientSide()) {
      return;
    }

    document.addEventListener('visibilitychange', visibilityCallback);
    document.addEventListener('mousemove', activityCallback);
    document.addEventListener('touchmove', activityCallback);
    return function () {
      document.removeEventListener('visibilitychange', visibilityCallback);
      document.removeEventListener('mousemove', activityCallback);
      document.removeEventListener('touchmove', activityCallback);
    };
  }, [activityCallback, visibilityCallback]); // Update and remove interval loop

  useEffect(function () {
    if (!isClientSide()) {
      return;
    }

    if (!activityLoopInterval.current) {
      activityLoopInterval.current = setInterval(runActivityLoop, activityLoopTime);
    }

    return function () {
      clearInterval(activityLoopInterval.current);
      activityLoopInterval.current = null;
    };
  }, []);
  return activityState;
} // This allows time for the browser to fire the onLoad callback if the
// image has already been loaded. Important for eliminating showing
// a flicker of loader when the image is already loaded.


var DELAY_DURATION_MS = 10; // The minimum amount of time the loader should be displayed.

var MIN_DURATION_MS = 700;

function useImageLoading(_ref) {
  var _ref$image = _ref.image,
      image = _ref$image === void 0 ? '' : _ref$image,
      _ref$delayDuration = _ref.delayDuration,
      delayDuration = _ref$delayDuration === void 0 ? DELAY_DURATION_MS : _ref$delayDuration,
      _ref$minDuration = _ref.minDuration,
      minDuration = _ref$minDuration === void 0 ? MIN_DURATION_MS : _ref$minDuration; // Initially we do not know whether or not the image has been loaded.

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isLoaded = _useState2[0],
      setIsLoaded = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      delayed = _useState4[0],
      setDelayed = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isMinLoaderDisplayPeriod = _useState6[0],
      setIsMinLoaderDisplayPeriod = _useState6[1]; // This is our logic for deciding whether or not an image loader should be displayed.


  var showLoader = !isLoaded && !delayed || isMinLoaderDisplayPeriod; // Handle setting a minimum loader display period.

  useEffect(function () {
    if (showLoader) {
      setIsMinLoaderDisplayPeriod(true);
      setTimeout(function () {
        return setIsMinLoaderDisplayPeriod(false);
      }, minDuration);
    }
  }, [showLoader]); // Handle image changes.

  useEffect(function () {
    if (image) {
      // when the image changes, we need to reset back to our initial state.
      setIsLoaded(false);
      setDelayed(true);
      setIsMinLoaderDisplayPeriod(false); // fire off a delay timer to set the delay flag to false,
      // i.e. give browser a little time to fire the onLoad
      // callback if the image is already loaded.

      setTimeout(function () {
        return setDelayed(false);
      }, delayDuration);
    }
  }, [image]); // This callback is fired when the browser is ready to render the image.

  var handleImageLoaded = function handleImageLoaded() {
    setTimeout(function () {
      return setIsLoaded(true);
    }, 0);
  };

  return {
    // Set this on the onLoad handler for the image.
    handleImageLoaded: handleImageLoaded,
    // Show the loader ONLY when this flag is true
    showLoader: showLoader
  };
}
/**
 * SSR friendly matchMedia wrapper for evaluating simple media queries.
 * The onChanged callback will be fired AFTER the first render and
 * then again ONLY when the evaluated query result changes.
 *
 * A good use case for this hook is as an alternative to listening to window
 * resize events as this event only fires when the media query result changes
 * so no throttling is required.
 *
 * NOTE:
 * CSS only media queries are still preferred over matchMedia whenever
 * possible so use this only where a standard CSS media query does not
 * make sense.
 */


function useMatchMedia(_ref) {
  var _ref$queryString = _ref.queryString,
      queryString = _ref$queryString === void 0 ? '' : _ref$queryString,
      _ref$onChanged = _ref.onChanged,
      onChanged = _ref$onChanged === void 0 ? function (matches) {} : _ref$onChanged;
  useEffect(function () {
    var changed = null;
    var mql = null;

    if (queryString) {
      changed = function changed(mql) {
        onChanged(mql.matches);
      };

      mql = window.matchMedia(queryString);
      onChanged(mql.matches);
      mql.addListener(changed);
    }

    return function () {
      if (changed && mql) {
        mql.removeListener(changed);
      }
    };
  }, [onChanged, queryString]);
}
/**
 * Modified ResponsiveModal hook that only runs when the window matches
 * a small screen size media query
 */


function useLockBodyScrollSmall(_ref) {
  var enabled = _ref.enabled;
  var overflow = useRef();
  var position = useRef();
  var top = useRef();
  var width = useRef();
  var pageYOffset = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      mqMatches = _useState2[0],
      setMQMatches = _useState2[1];

  var setMatchMedia = useCallback(function (matches) {
    setMQMatches(matches);
  });
  useMatchMedia({
    queryString: QUERY_FOR_PHONE_WIDTH,
    onChanged: setMatchMedia
  });

  var setup = function setup(isIOS) {
    // Store the current styles and scroll offset.
    overflow.current = document.body.style.overflow;
    position.current = document.body.style.position;
    top.current = document.body.style.top;
    width.current = document.body.style.width;
    pageYOffset.current = window.pageYOffset; // This locks body scroll on everything except iOS.

    document.body.style.overflow = 'hidden';

    if (isIOS) {
      // Setting position:fixed is the only way to ensure the underneath modal content does not
      // scroll on iOS. While it accomplishes this goal it has the unfortunate side effect of
      // scrolling the underneath modal content to the top of the page. Note that there are some
      // claims that iOS 13 does not have this issue but those claims are false. In particular,
      // the underneath content WILL scroll when the browser chrome is hidden.
      document.body.style.position = 'fixed'; // This counteracts the undersired scroll to top behavior by re-applying the scroll.

      document.body.style.top = -pageYOffset.current + 'px'; // When we set position:fixed we also need to ensure the body is full width
      // otherwise elements may jump horizontally.

      document.body.style.width = '100%';
    }
  };

  var reset = function reset(isIOS) {
    // Restore all the styles we changed when displaying the modal.
    document.body.style.overflow = overflow.current;

    if (isIOS) {
      document.body.style.position = position.current;
      document.body.style.top = top.current;
      document.body.style.width = width.current;

      if (typeof window.scrollTo === 'function') {
        window.scrollTo(0, pageYOffset.current);
      }
    }
  };

  var getIsIOS = function getIsIOS() {
    // iOS requires extra styling. We do not want to apply this extra
    // styling when its not needed because it can cause unexpected
    // side effects for no benefit. So we need to first detect if we
    // are currently running on an iOS device.
    // https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
    return navigator && navigator.platform && (/iPad|iPhone|iPod/.test(navigator.platform) || // iOS 12 and below
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 // iOS 13
    ) && !window.MSStream; // microsoft tried to fool gmail by injecting iPhone in their user agent;
  };

  useEffect(function () {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      var isIOS = getIsIOS();
      var isEnabled = enabled && mqMatches;
      isEnabled ? setup(isIOS) : reset(isIOS);
      return function () {
        reset(isIOS);
      };
    }
  }, [enabled, mqMatches]);
}
/**
 * Utility hook for attaching a long touch event listener to a component.
 *
 * @param onHold function triggered whenever the event target is being held for longer than the delay
 *                specified in {@param options}.
 * @param onTap function triggered whenever the event target is just being tapped. Acts as an
 *              onClick.
 * @param options additional options for the event handling:
 *          - shouldPreventDefault - if default event behaviour should be prevented when the long touch is finished.
 *          - delay - minimum time in milliseconds required in order to trigger {@param onHold}.
 */


function useLongTouch(onHold, onTap) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    shouldPreventDefault: true,
    delay: 300
  };

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      longTouchTriggered = _useState2[0],
      setLongTouchTriggered = _useState2[1];

  var timeout = useRef(null);
  var target = useRef(null);
  var shouldPreventDefault = options.shouldPreventDefault,
      delay = options.delay;
  var start = useCallback(function (event) {
    target.current = event.target;
    timeout.current = setTimeout(function () {
      onHold(event);
      setLongTouchTriggered(true);
    }, delay);
  }, [onHold, delay]);
  var clear = useCallback(function (event) {
    timeout.current && clearTimeout(timeout.current);

    if (!longTouchTriggered) {
      onTap(event);
    } else {
      shouldPreventDefault && event.preventDefault();
    }

    setLongTouchTriggered(false);
  }, [onTap, longTouchTriggered]);
  return {
    onTouchStart: function onTouchStart(e) {
      return start(e);
    },
    onTouchEnd: function onTouchEnd(e) {
      return clear(e);
    }
  };
}
/**
 * This hook uses an intersection observer to keep track
 * of whether or not a target element is currently visible
 * in the document viewport.
 *
 * The 'isOnScreen' return value is updated on exactly 2 conditions:
 *  (1) the target element transitions from being fully or partially
 *      visible to completely offscreen, i.e., 0% visible.
 *  (2) the target element transitions from being fully offscreen to
 *      partially visible.
 */


function useOnScreen(_ref) {
  var _ref$targetEl = _ref.targetEl,
      targetEl = _ref$targetEl === void 0 ? null : _ref$targetEl,
      _ref$initialValue = _ref.initialValue,
      initialValue = _ref$initialValue === void 0 ? false : _ref$initialValue; // State and setter for storing whether target element is on screen.

  var _useState = useState(initialValue),
      _useState2 = _slicedToArray(_useState, 2),
      isOnScreen = _useState2[0],
      setIsOnScreen = _useState2[1];

  useEffect(function () {
    if (targetEl) {
      // Create the intersection observer.
      var observer = new window.IntersectionObserver( // Update our state when the intersection observer callback fires
      function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 1),
            entry = _ref3[0];

        setIsOnScreen(entry.isIntersecting);
      }, // Intersection observer options.
      // The options are configured to trigger state updates whenvever
      // the target element partially enters or completely leaves the document viewport.
      {
        // Watch for changes in visibility of the target element relative
        // to the document's viewport
        root: null,
        // Watch for changes in the intersection between the target element's bounds
        // and the root element, with optional added (or subtracted) space.
        // Default is no added or subtracted space.
        rootMargin: '0px',
        // 0% threshold is used for detecting when target element transitions
        // from onscreen to offscreen and vice versa.
        threshold: 0.0
      }); // Start observing the target element.

      observer.observe(targetEl); // Cleanup the intersection observer when component is unmounted.

      return function () {
        observer.unobserve(targetEl);
      };
    }
  }, [targetEl]); // only run effect when the target element changes

  return isOnScreen;
}

function useOutsideClick(_ref) {
  var _ref$element = _ref.element,
      element = _ref$element === void 0 ? null : _ref$element,
      _ref$onOutsideClick = _ref.onOutsideClick,
      onOutsideClick = _ref$onOutsideClick === void 0 ? function () {} : _ref$onOutsideClick;
  useEffect(function () {
    if (element) {
      var handleOutsideClick = function handleOutsideClick(jsEvent) {
        var isClickInside = element.contains(jsEvent.target);

        if (!isClickInside) {
          onOutsideClick();
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      return function () {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, [element]);
}
/**
 * Performance safe window resize/scroll event listener custom hook.
 * Scroll/Resize callbacks are throttled using lodash throttle and the
 * event listeners are removed when the component unmounts.
 */


function useSafeResizeScrollEventListener(_ref) {
  var _ref$listen = _ref.listen,
      listen = _ref$listen === void 0 ? true : _ref$listen,
      _ref$onUpdate = _ref.onUpdate,
      onUpdate = _ref$onUpdate === void 0 ? function () {} : _ref$onUpdate;
  useEffect(function () {
    if (listen) {
      var handleUpdate = throttle(function () {
        onUpdate();
      });
      window.addEventListener('resize', handleUpdate);
      window.addEventListener('scroll', handleUpdate);
      return function () {
        window.removeEventListener('resize', handleUpdate);
        window.removeEventListener('scroll', handleUpdate);
      };
    }
  }, [listen, onUpdate]);
}

function useTextAreaResize(_ref) {
  var autosize = _ref.autosize,
      error = _ref.error,
      helperText = _ref.helperText,
      value = _ref.value,
      maxHeight = _ref.maxHeight,
      minHeight = _ref.minHeight,
      onInputChange = _ref.onInputChange,
      _ref$enableScrollOver = _ref.enableScrollOverride,
      enableScrollOverride = _ref$enableScrollOver === void 0 ? false : _ref$enableScrollOver;
  useLayoutEffect(function () {
    window.addEventListener('resize', handleDocumentResize);
    return function () {
      window.removeEventListener('resize', handleDocumentResize);
    };
  });
  var handleDocumentResize = throttle(function () {
    resizeBox();
  });

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      cursorPosition = _useState2[0],
      setCursorPosition = _useState2[1];

  var _useState3 = useState(enableScrollOverride),
      _useState4 = _slicedToArray(_useState3, 2),
      canScroll = _useState4[0],
      setCanScroll = _useState4[1];

  var textareaRef = useRef(null);
  var subtextHasContent = !!error || !!helperText;
  var handleInputChange = useCallback(function (e) {
    setCursorPosition(e.target.selectionStart);
    onInputChange({
      jsEvent: e,
      value: e.target.value
    });
  }, [onInputChange, setCursorPosition]);
  useEffect(function () {
    var textBoxEl = textareaRef.current;

    if (textBoxEl && document.activeElement === textBoxEl) {
      // Update the cursor position
      textBoxEl.selectionStart = cursorPosition;
      textBoxEl.selectionEnd = cursorPosition;
    }
  }, [textareaRef, value]);
  useLayoutEffect(function () {
    resizeBox();
  }, [value, maxHeight]);

  var resizeBox = function resizeBox() {
    var textBoxEl = textareaRef.current; // Reset the text area ref to use auto to properly get the scrollHeight

    textBoxEl.style.height = 'auto';

    if (!autosize) {
      return false;
    }

    var calculatedHeight = calculateHeight();
    var heightToSet = maxHeight;

    if (calculatedHeight < maxHeight) {
      heightToSet = calculatedHeight;
      setCanScroll(false);
    } else {
      heightToSet = maxHeight;
      setCanScroll(true);
    } // This is necessary for deletion of text, as scrollHeight
    // doesn't reset to 0 as expected when content is deleted.


    if (!value) {
      return;
    }

    textBoxEl.style.height = "".concat(heightToSet, "px");
  };

  var calculateHeight = function calculateHeight() {
    var textBox = textareaRef.current;

    if (textBox) {
      // scrollHeight provides the height of the content, even if scrollbars are
      // present without including padding height
      return textBox.scrollHeight;
    }

    return minHeight;
  }; // Callback for set ref, allows us to calculate the height when the ref is first set


  var setRef = useCallback(function (node) {
    if (node) {
      calculateHeight();
    }

    textareaRef.current = node;
  }, []);
  return {
    canScroll: canScroll,
    handleInputChange: handleInputChange,
    setRef: setRef,
    subtextHasContent: subtextHasContent
  };
}
/**
 * This is a hook created to call a callback
 * when a scroll container hits the bottom
 */


function useScrollBottomTrigger(_ref) {
  var callback = _ref.callback,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 1 : _ref$offset;
  var scrollRef = useRef(null);
  var onScroll = useCallback(throttle(function () {
    var scrollEl = scrollRef.current;

    if (scrollEl) {
      var shouldTrigger = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight * offset;

      if (shouldTrigger && callback && typeof callback === 'function') {
        callback();
      }
    }
  }), [callback, offset, scrollRef]); // We'll use a functional ref to control adding/removing event listeners

  var setRef = useCallback(function (node) {
    if (node) {
      node.addEventListener('scroll', onScroll, false);
    } else if (scrollRef.current) {
      scrollRef.current.removeEventListener('scroll', onScroll);
    }

    scrollRef.current = node;
  }, [onScroll, scrollRef]); // Attach this ref to the scroll container

  return {
    ref: scrollRef,
    setRef: setRef
  };
}

export { useComponentStateToggle, useDocumentActive, useImageLoading, useIsomorphicLayoutEffect, useLockBodyScrollSmall, useLongTouch, useMatchMedia, useOnScreen, useOutsideClick, useSafeResizeEventListener, useSafeResizeScrollEventListener, useScrollBottomTrigger, useTextAreaResize, useValueHasChanged };